// src/services/invoiceService.js
// Production-grade Invoices service

import api from '../api/axios';
import { normalizeToArray } from '../api/normalizeResponse';

/* --------------------------------- utils ---------------------------------- */
const pickPayload = (res) => (res?.data?.data ?? res?.data ?? {});
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const getBackendRoot = () => {
  const base = api?.defaults?.baseURL;
  try {
    if (base && /^https?:\/\//i.test(base)) {
      const u = new URL(base);
      return `${u.origin}`;
    }
  } catch (_) {}
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
};

const getFilenameFromHeaders = (headers = {}) => {
  const cd = headers['content-disposition'] || headers['Content-Disposition'];
  if (!cd || typeof cd !== 'string') return null;
  const utf8 = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8 && utf8[1]) return decodeURIComponent(utf8[1]);
  const quoted = cd.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted && quoted[1]) return quoted[1];
  const bare = cd.match(/filename\s*=\s*([^;]+)/i);
  if (bare && bare[1]) return bare[1].trim();
  return null;
};

/* ------------------------------ normalizer -------------------------------- */
export const resolveInvoiceId = (row) => row?.invoiceId || row?._id || row?.id;

const normalizeInvoice = (inv = {}) => {
  const id = inv._id || inv.id || inv.invoiceId;

  const workObj = inv.work || inv.job || null;
  const quoteObj = inv.quote || null;

  const workId =
    inv.workId ||
    workObj?._id ||
    workObj?.id ||
    null;

  const workNumber =
    inv.workNumber ||
    workObj?.workNumber ||
    workObj?.workNo ||
    null;

  const quoteId =
    inv.quoteId ||
    quoteObj?._id ||
    quoteObj?.id ||
    null;

  const quoteNumber =
    inv.quoteNumber ||
    quoteObj?.quoteNumber ||
    quoteObj?.number ||
    null;

  const invoiceNumber =
    inv.invoiceNumber ||
    inv.number ||
    inv.code ||
    null;

  // Respect backend-computed totals bag when present (public invoices, etc.)
  const totals = inv.totals || {};
  const rawSubtotal =
    inv.subtotal != null ? inv.subtotal : totals.subtotal;
  const rawTaxTotal =
    inv.taxTotal != null ? inv.taxTotal : totals.taxTotal;
  const rawDiscountTotal =
    inv.discountTotal != null ? inv.discountTotal : totals.discountTotal;
  const rawTotalAmount =
    inv.totalAmount != null
      ? inv.totalAmount
      : (totals.totalAmount != null
          ? totals.totalAmount
          : inv.total);

  const currencyGuess =
    inv.currency ||
    totals.currency ||
    'USD';

  return {
    ...inv,
    id,
    _id: id,
    invoiceId: id,

    invoiceNumber,
    status: inv.status || inv.lifecycleStatus || 'unpaid',

    issueDate: inv.issueDate || inv.issuedAt || inv.createdAt || null,
    dueDate: inv.dueDate || inv.due || null,
    paidAt: inv.paidAt || null,

    currency: String(currencyGuess).toUpperCase(),

    totalAmount: toNum(rawTotalAmount),
    subtotal: toNum(rawSubtotal),
    taxTotal: toNum(rawTaxTotal),
    discountTotal: toNum(rawDiscountTotal),

    workId,
    workNumber,
    quoteId,
    quoteNumber,
  };
};

/* ---------- meta-safe list parsing (handles double-wrapped + interceptors) -- */
function parseList(payloadIn) {
  // Shapes we see:
  //  A) Axios interceptor: { data: [...], _meta: {...} }
  //  B) API classic:       { data: [...], meta: {...} }
  //  C) Variants:          { items:[...] } / { results:[...] } / double-wrapped
  //  D) Raw array:         [...]
  let list = [];
  let metaRaw = null;

  const p = payloadIn ?? {};

  if (Array.isArray(p)) {
    list = p;
    metaRaw = p.meta ?? null;
  } else if (Array.isArray(p.data)) {
    // ✅ Prefer axios interceptor stash when present
    list = p.data;
    metaRaw = p._meta ?? p.meta ?? p.pagination ?? null;
  } else if (Array.isArray(p.items)) {
    list = p.items;
    metaRaw = p.meta ?? p.pagination ?? null;
  } else if (Array.isArray(p.results)) {
    list = p.results;
    metaRaw = p.meta ?? p.pagination ?? null;
  } else if (p?.data && typeof p.data === 'object') {
    // double-wrapped: { data: { data:[...], meta:{...} } }
    const inner = p.data;
    if (Array.isArray(inner.data)) {
      list = inner.data;
      metaRaw = inner.meta ?? p.meta ?? p._meta ?? null;
    } else if (Array.isArray(inner.items)) {
      list = inner.items;
      metaRaw = inner.meta ?? p.meta ?? p._meta ?? null;
    } else if (Array.isArray(inner.results)) {
      list = inner.results;
      metaRaw = inner.meta ?? p.meta ?? p._meta ?? null;
    }
  }

  if (!Array.isArray(list)) {
    list = normalizeToArray(p?.data ?? p?.items ?? p?.results ?? p ?? []);
  }

  const items = list.map(normalizeInvoice);

  // Coerce and fallback (but DO NOT cap totals to items.length)
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : undefined);

  const page  = num(metaRaw?.page)  ?? 1;
  const limit = num(metaRaw?.limit) ?? (items.length || 25);
  const total = num(metaRaw?.total) ?? (Array.isArray(items) ? items.length : 0);

  const totalPages =
    (num(metaRaw?.totalPages) && num(metaRaw?.totalPages) > 0)
      ? num(metaRaw?.totalPages)
      : (total > 0 && limit > 0 ? Math.ceil(total / limit) : 1);

  const meta = { ...(metaRaw || {}), page, limit, total, totalPages };
  return { items, meta };
}

// Relation hints (harmless if API ignores them)
const RELATION_HINTS = {
  include: ['client', 'work', 'quote'],
  populate: ['client', 'work', 'quote'],
  expand: ['client', 'work', 'quote'],
};

/* -------------------------------- list/fetch ------------------------------- */
export async function listInvoices(params = {}) {
  const resp = await api.get('/invoices', { params: { ...RELATION_HINTS, ...params } });

  // If interceptor normalized, `resp.data` is array and meta is in `resp._meta`.
  // Pass the whole axios response so parseList can read `_meta`.
  const payload =
    (resp && typeof resp === 'object' && Array.isArray(resp?.data)) ||
    (resp && typeof resp === 'object' && 'meta' in resp)
      ? resp
      : (resp?.data ?? resp);

  return parseList(payload);
}

export async function getInvoice(id) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.get(`/invoices/${id}`, { params: RELATION_HINTS });
  const data = resp?.data?.data || resp?.data || resp || null;
  // If an interceptor returned `data` directly, `resp` is already the payload.
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : null;
}

/* -------------------------------- mutations -------------------------------- */
export async function createInvoice(payload) {
  const resp = await api.post('/invoices', payload);
  const data = resp?.data?.data || resp?.data || resp || null;
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : null;
}

export async function updateInvoice(id, payload) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.put(`/invoices/${id}`, payload);
  const data = resp?.data?.data || resp?.data || resp || null;
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : null;
}

export async function deleteInvoice(id) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.delete(`/invoices/${id}`);
  const data = resp?.data?.data || resp?.data || resp || null;
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : true;
}

/* --------------------------- send / prepare flow --------------------------- */
export async function prepareSendInvoice(id) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.get(`/invoices/${id}/send/prepare`);
  return pickPayload(resp);
}

export async function sendInvoice(id, body = {}) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  try {
    const resp = await api.post(`/invoices/${id}/send`, body);
    const data = pickPayload(resp);
    const status = data.status || (resp?.status === 202 ? 'queued' : 'ok');
    const queued =
      typeof data.queued === 'boolean'
        ? data.queued
        : (resp?.status === 202 || status === 'queued');

    return {
      ok: true,
      queued,
      status,
      messageKey: data.messageKey ?? data.id ?? null,
      providerMessageId: data.providerMessageId ?? null,
      invoice: data.invoice ? normalizeInvoice(data.invoice) : undefined,
      raw: data,
    };
  } catch (e) {
    const code = e?.response?.status;

    if (code === 409) {
      const data = pickPayload(e.response);
      return {
        ok: true,
        queued: false,
        status: 'duplicate',
        alreadySent: true,
        retryAfter: Number(e.response?.headers?.['retry-after'] || 0) || null,
        raw: data,
      };
    }

    if (code === 429) {
      const data = pickPayload(e.response);
      return {
        ok: false,
        rateLimited: true,
        retryAfter: Number(e.response?.headers?.['retry-after'] || 0) || null,
        raw: data,
      };
    }

    throw e;
  }
}

export async function voidInvoice(id, body = {}) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.post(`/invoices/${id}/void`, body);
  const data = resp?.data?.data || resp?.data || resp || null;
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : true;
}

export async function writeOffInvoice(id, body = {}) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.post(`/invoices/${id}/write-off`, body);
  const data = resp?.data?.data || resp?.data || resp || null;
  const entity = data?.data && !data._id ? data.data : data;
  return entity ? normalizeInvoice(entity) : true;
}

/* ------------------------- internal PDF helpers (UI) ----------------------- */
export async function ensureStoredInvoicePdf(id, { force = false } = {}) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.post(`/invoices/${id}/pdf/ensure`, { force });
  const data = pickPayload(resp);
  return {
    fileKey: data.fileKey ?? data.key ?? null,
    fileName: data.fileName ?? data.filename ?? data.name ?? null,
    fileUrl: data.fileUrl ?? data.url ?? null,
    size: toNum(data.size),
    version: data.version ?? data.v ?? null,
    exists: data.exists ?? !!(data.fileUrl || data.fileKey),
    raw: data,
  };
}

export async function getStoredInvoicePdfMeta(id) {
  if (!id) throw new Error('INVOICE_ID_REQUIRED');
  const resp = await api.get(`/invoices/${id}/pdf/meta`).catch(() => null);
  const data = resp ? pickPayload(resp) : null;
  if (!data) {
    return {
      exists: false,
      fileName: null,
      fileKey: null,
      fileUrl: null,
      size: 0,
      version: null,
    };
  }
  return {
    fileKey: data.fileKey ?? data.key ?? null,
    fileName: data.fileName ?? data.filename ?? data.name ?? null,
    fileUrl: data.fileUrl ?? data.url ?? null,
    size: toNum(data.size),
    version: data.version ?? data.v ?? null,
    exists: data.exists ?? !!(data.fileUrl || data.fileKey),
    raw: data,
  };
}

/* ------------------------------ public flows ------------------------------- */

/**
 * Public invoice fetch used by PublicInvoiceView.
 * Hits JSON endpoint: GET /api/public/invoice/:token/json
 */
export async function fetchPublicInvoice(token) {
  if (!token) throw new Error('TOKEN_REQUIRED');
  const root = getBackendRoot();
  const url = `${root}/api/public/invoice/${encodeURIComponent(token)}/json`;
  const resp = await api.get(url);
  const data = pickPayload(resp);
  const rawInv = data.invoice ?? data;
  const invoice = rawInv ? normalizeInvoice(rawInv) : null;
  return {
    invoice,
    meta: data.meta ?? null,
    links: data.links ?? null,
    raw: data,
  };
}

/**
 * Public pay-click metric only (no redirect, no Stripe link here).
 * POST /api/public/invoice/:token/pay-click
 */
export async function trackPayClick(token, meta = {}) {
  if (!token) throw new Error('TOKEN_REQUIRED');
  const root = getBackendRoot();
  const url = `${root}/api/public/invoice/${encodeURIComponent(
    token,
  )}/pay-click`;
  const resp = await api.post(url, meta);
  return { ok: true, raw: pickPayload(resp) };
}

/**
 * Public invoice PDF download/redirect.
 * GET /api/public/invoice/:token/pdf
 */
export async function downloadPdfSigned(token) {
  if (!token) throw new Error('TOKEN_REQUIRED');
  const root = getBackendRoot();
  const url = `${root}/api/public/invoice/${encodeURIComponent(token)}/pdf`;
  const resp = await api.get(url, { responseType: 'blob' });
  const blob = resp?.data;
  const filename = getFilenameFromHeaders(resp?.headers) || 'invoice.pdf';
  return { blob, filename, url };
}

/**
 * Create Stripe Checkout session for a public invoice.
 * POST /api/public/invoices/:publicToken/pay-session
 *
 * Returns:
 *   { url, sessionId, raw }
 */
export async function createPublicInvoicePaySession(token) {
  if (!token) throw new Error('TOKEN_REQUIRED');
  const root = getBackendRoot();
  const url = `${root}/api/public/invoices/${encodeURIComponent(
    token,
  )}/pay-session`;

  const resp = await api.post(url, {});
  const data = pickPayload(resp);

  return {
    url: data.url || data.redirectUrl || data.checkoutUrl || null,
    sessionId: data.sessionId || data.id || null,
    raw: data,
  };
}
